import { FileIcon, useDownloadFile } from '@newfront-insurance/admin-ui/files';
import type { GeneratedFileInfo } from '@newfront-insurance/ai-api';
import { cn } from '@newfront-insurance/core-ui/v2';
import type { ToolUIPart } from 'ai';

interface ToolInvocationPart {
  type: string;
  state?: string;
  toolCallId?: string;
  output?: {
    files?: Array<{
      fileId: string;
      filename: string;
      containerId: string;
    }>;
    containerId?: string;
    image?: {
      mediaType: string;
      base64Data: string;
    };
    generatedFileUuids?: string[];
    generatedFiles?: Array<GeneratedFileInfo>;
  };
}

/**
 * Helper function to render generated files from tool output
 */
function renderGeneratedFiles(output: ToolInvocationPart['output']): React.ReactNode | null {
  if (!output) return null;

  // Use generated files from Files service
  if (output.generatedFiles && output.generatedFiles.length > 0) {
    const generatedFiles = output.generatedFiles.map((file) => ({
      uuid: file.fileUuid,
      filename: file.filename,
    }));
    return <GeneratedFilesList files={generatedFiles} />;
  }

  // Fallback to generatedFileUuids
  if (output.generatedFileUuids && output.generatedFileUuids.length > 0) {
    const generatedFiles = output.generatedFileUuids.map((uuid, idx) => ({
      uuid,
      filename: `Generated file ${idx + 1}`,
    }));
    return <GeneratedFilesList files={generatedFiles} />;
  }

  return null;
}

interface ProcessFilesToolProps {
  part: ToolUIPart;
}

/**
 * Component for rendering processFiles tool output.
 */
export function ProcessFilesTool({ part }: ProcessFilesToolProps) {
  // Handle output
  if (part.state === 'output-available' && (part as ToolInvocationPart).output) {
    return renderGeneratedFiles((part as ToolInvocationPart).output);
  }

  return null;
}

export function GeneratedFilesList({ files }: { files: { uuid: string; filename: string }[] }) {
  return (
    <div className="flex flex-col gap-2">
      <div className="text-muted-foreground text-sm font-medium">Generated Files:</div>
      <div className="flex flex-row flex-wrap gap-2">
        {files.map((file) => (
          <ChatFileListItem key={file.uuid} file={file} />
        ))}
      </div>
    </div>
  );
}

function ChatFileListItem({ file }: { file: { uuid: string; filename: string } }) {
  const { download, isDownloading } = useDownloadFile({});

  const handleClick = () => {
    download(file.uuid);
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
      className={cn(
        'flex w-fit max-w-xs cursor-pointer items-center gap-2 rounded-sm px-1 py-1',
        'hover:bg-muted/50 transition-colors',
        { 'pointer-events-none opacity-50': isDownloading },
      )}
    >
      <FileIcon filename={file.filename} hasBackground={false} />
      <span className="text-steel-400 truncate text-xs">{file.filename}</span>
    </div>
  );
}
